import { useEffect, useState, useCallback, useRef } from 'react';
import { infrastructureService } from '../services/api';

interface LightStatus {
  comprasnet_session?: {
    logged_in?: boolean;
    last_ok_at?: string | null;
    minutes_ago?: number | null;
  };
  /** Estado do JWT cnetmobile — a perna que os ROBÔS usam (ver classify). */
  comprasnet_jwt?: {
    valid?: boolean | null;
    exp_in_sec?: number | null;
    minutes_ago?: number | null;
    afeta_robos?: boolean;
  };
  comprasnet_paused?: { paused?: boolean };
  proposta_extension?: {
    alive?: boolean;
    queue_size?: number | null;
    last_poll_ago_ms?: number | null;
  };
}

type Health = 'green' | 'yellow' | 'red' | 'unknown';

function classify(s: LightStatus): { color: Health; reason: string } {
  if (s?.comprasnet_paused?.paused) return { color: 'yellow', reason: 'ComprasNet pausado' };
  if (!s?.proposta_extension?.alive) return { color: 'red', reason: 'Extension offline' };
  const sessionOk = s?.comprasnet_session?.logged_in === true;
  const min = s?.comprasnet_session?.minutes_ago;
  if (!sessionOk) return { color: 'yellow', reason: min != null ? `Sessão expirada (último OK há ${min}min)` : 'Sessão expirada' };
  // ⚠️ Cookie vivo NÃO significa robô operante. São duas pernas que morrem
  // separadas: em 11/08/2026 este ponto ficou VERDE com o JWT do cnetmobile 51
  // min vencido (`no_valid_jwt, expIn=-3077s`) e os robôs falhando "do nada".
  // Verde agora exige as duas.
  if (s?.comprasnet_jwt?.afeta_robos) {
    const exp = s.comprasnet_jwt.exp_in_sec;
    const venc = exp != null && exp < 0 ? ` há ${Math.round(-exp / 60)}min` : '';
    return { color: 'yellow', reason: `JWT vencido${venc} (robôs afetados) — sessão do navegador OK` };
  }
  return { color: 'green', reason: `Sessão OK (${min ?? 0}min)` };
}

// R1.18: instruções de reconexão por tipo de problema (banner acionável).
function fixSteps(color: Health, reason: string): string[] {
  if (reason.includes('Extension offline')) {
    return [
      'Abra o Firefox no bot VNC (localhost:6080).',
      'Vá em about:debugging → Este Firefox.',
      'Na extensão ComprasNet, clique em Recarregar.',
      'Confirme que a versão atualizou e clique "Re-testar agora".',
    ];
  }
  if (reason.includes('pausado')) {
    return ['ComprasNet está pausado nas configurações. Despause em /admin/infraestrutura para retomar.'];
  }
  if (reason.includes('JWT vencido')) {
    return [
      'O login do navegador está vivo, mas o token que os robôs usam expirou.',
      'O heartbeat tenta renovar sozinho a cada 3 min (warm-jwt, sem captcha).',
      'Persistindo, use "Renovar sessão" — faz login completo no gov.br.',
      'Não inicie disputa nem envio de proposta enquanto estiver amarelo.',
    ];
  }
  if (reason.includes('Sessão')) {
    return [
      'A sessão do ComprasNet expirou.',
      'No Firefox do VNC (localhost:6080), faça login novamente em compras.gov.br.',
      'Depois clique "Re-testar agora".',
    ];
  }
  return ['Verifique o bot em /admin/infraestrutura.'];
}

interface Props {
  onClick?: () => void;
  variant?: 'compact' | 'inline';
}

export default function ComprasNetHealthDot({ onClick, variant = 'compact' }: Props) {
  const [status, setStatus] = useState<LightStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [relogging, setRelogging] = useState(false);
  const [showPopover, setShowPopover] = useState(false);
  const aliveRef = useRef(true);

  const fetchStatus = useCallback(async () => {
    try {
      const data = await infrastructureService.getLightStatus();
      if (aliveRef.current) setStatus(data);
    } catch {
      if (aliveRef.current) setStatus(null);
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    fetchStatus();
    const id = setInterval(fetchStatus, 30000);
    return () => { aliveRef.current = false; clearInterval(id); };
  }, [fetchStatus]);

  const handleRetry = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    setRefreshing(true);
    await fetchStatus();
    setTimeout(() => setRefreshing(false), 400);
  }, [fetchStatus]);

  // Renovar sessão: login COMPLETO no gov.br (~1-2min). Pro user destravar a
  // sessão sozinho quando expira. (hardening 29/05/2026)
  const handleRelogin = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    setRelogging(true);
    try {
      const r = await infrastructureService.forceReloginNew();
      if (r?.ok) await fetchStatus();
    } catch { /* timeout/erro — status segue mostrando o estado real */ }
    finally { if (aliveRef.current) setRelogging(false); }
  }, [fetchStatus]);

  const { color, reason } = status ? classify(status) : { color: 'unknown' as Health, reason: 'Carregando...' };
  const bg = color === 'green' ? 'bg-emerald-500' : color === 'yellow' ? 'bg-amber-500' : color === 'red' ? 'bg-red-500' : 'bg-slate-500';
  const ring = color === 'green' ? 'ring-emerald-500/40' : color === 'yellow' ? 'ring-amber-500/40' : color === 'red' ? 'ring-red-500/40' : 'ring-slate-500/40';
  const problem = color === 'red' || color === 'yellow';

  if (variant === 'inline') {
    const borderColor = color === 'green' ? 'border-emerald-500/40 bg-emerald-500/5' : color === 'yellow' ? 'border-amber-500/40 bg-amber-500/5' : color === 'red' ? 'border-red-500/40 bg-red-500/5' : 'border-slate-600 bg-slate-700/30';
    const textColor = color === 'green' ? 'text-emerald-300' : color === 'yellow' ? 'text-amber-300' : color === 'red' ? 'text-red-300' : 'text-slate-300';
    return (
      <div className="relative">
        <button
          onClick={() => problem ? setShowPopover((v) => !v) : onClick?.()}
          title={problem ? 'Clique para ver como reconectar' : 'Click para ver detalhes em /admin/infraestrutura'}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${borderColor} ${textColor} text-sm transition-colors hover:opacity-80`}
        >
          <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${bg} ring-4 ${ring}`}>
            {color === 'green' && !loading && (
              <span className={`absolute inset-0 rounded-full ${bg} opacity-75 animate-ping`}></span>
            )}
          </span>
          <span className='font-medium'>ComprasNet: {reason}</span>
          {problem && <span className="text-[10px] opacity-70">▾</span>}
        </button>
        {problem && showPopover && (
          <div className="absolute right-0 top-full mt-1 z-50 w-80 bg-slate-800 border border-slate-600 rounded-lg shadow-xl p-3 text-left">
            <div className={`text-sm font-semibold ${textColor} mb-1.5`}>{reason}</div>
            <ol className="list-decimal list-inside space-y-1 text-xs text-slate-300 mb-2.5">
              {fixSteps(color, reason).map((s, i) => <li key={i}>{s}</li>)}
            </ol>
            <div className="flex items-center gap-2">
              <button
                onClick={handleRetry}
                disabled={refreshing || relogging}
                className="px-2.5 py-1 rounded bg-cyan-600/30 hover:bg-cyan-600/50 border border-cyan-500/40 text-cyan-200 text-xs font-medium disabled:opacity-50"
              >
                {refreshing ? 'Testando…' : '↻ Re-testar agora'}
              </button>
              {(reason.includes('Sessão') || reason.includes('JWT')) && (
                <button
                  onClick={handleRelogin}
                  disabled={relogging}
                  title="Faz login completo no gov.br (~1-2 min) e renova a sessão automaticamente"
                  className="px-2.5 py-1 rounded bg-amber-600/30 hover:bg-amber-600/50 border border-amber-500/40 text-amber-200 text-xs font-medium disabled:opacity-50"
                >
                  {relogging ? '⏳ Renovando…' : '🔄 Renovar sessão'}
                </button>
              )}
              {onClick && (
                <button onClick={(e) => { e.stopPropagation(); setShowPopover(false); onClick(); }} className="px-2.5 py-1 rounded bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs">
                  Abrir infraestrutura
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <button
      onClick={onClick}
      title={`ComprasNet: ${reason}`}
      className='flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-slate-800 text-slate-300 transition-colors'
    >
      <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${bg} ring-4 ${ring}`}>
        {color === 'green' && !loading && (
          <span className={`absolute inset-0 rounded-full ${bg} opacity-75 animate-ping`}></span>
        )}
      </span>
      <span className='text-xs font-medium hidden lg:block'>ComprasNet</span>
    </button>
  );
}
